import { AfterViewInit, Component, OnInit, Optional, QueryList, Self, ViewChildren } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormControl, FormGroup, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { IMyInputFieldChanged } from '@nodro7/angular-mydatepicker';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { DateFormatter } from '../../../common/date-utils.service';
import { PresentationMode } from '../../../common/enums';
import { FixDateConverter } from '../../../common/fixDate.service';
import { FormSubGroupControl } from '../../../models/formControls/FormSubGroupControl';
import { VisumErfassungComponentBase } from '../../visum-erfassung-component-base';

@Component({
  selector: 'app-visum-erfassung-sub-form',
  templateUrl: './visum-erfassung-sub-form.component.html',
  styleUrls: ['./visum-erfassung-sub-form.component.scss']
})
export class VisumErfassungSubFormComponent extends VisumErfassungComponentBase
  implements OnInit, ControlValueAccessor, Validator, AfterViewInit {

  form: FormGroup;
  presentationModeEnum = PresentationMode;

  private delayedDatePickerValues: { [key: string]: any } = {};

  // wird im workaround benutzt, um für Felder die maximale Länge zu ermitteln
  // sollte grösser oder gleich dem grössten Textfeld sein
  private longText = '^'.repeat(20001);

  protected dateFixer: FixDateConverter;
  protected dateFormatter: DateFormatter;

  onTouched: () => void = () => {};

  @ViewChildren(NgbPopover)
  private assistantPopoverList: QueryList<NgbPopover>;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private assistantPopoverService: AssistantPopoverService
  ) {
    super();
    if (this.controlDir != null) {
      this.controlDir.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.controlDir) {
      const control = this.controlDir.control as FormSubGroupControl;
      if (control) {
        control._valueAccessor = this;
      }
    }
  }

  ngAfterViewInit(): void {
    this.assistantPopoverService.initializeAssistant(this.assistantPopoverList);
  }

  /**
   * Setzt die Werte des Formulars basierend auf dem gegebenen Wert.
   * @param value Der Wert, der in das Formular geschrieben werden soll
   */
  writeValue(value: any): void {
    const controls = this.form.controls;
    value = value || {};
    this.write(controls, value);
    this.onValidatorUpdate();
  }

  private write(controls:  {[key: string]: AbstractControl<any, any>}, value: any) {
    Object.keys(controls).forEach(fieldName => {
      const ctrl = controls[fieldName];
      if (ctrl instanceof FormGroup) {
        this.write(ctrl.controls, value);
        return;
      }
      let fieldValue = value[fieldName];
      let isDirty = false;

      if (fieldValue == null) {
        fieldValue = ctrl instanceof FormArray ? [] : null;
      } else {
        isDirty = true;
      }

      if (ctrl instanceof FormArray) {
        const newLength = fieldValue.length;
        while (ctrl.length < newLength) {
          ctrl.push(new FormSubGroupControl());
        }
        while (ctrl.length > newLength) {
          ctrl.removeAt(ctrl.length - 1);
        }
      }

      ctrl.setValue(fieldValue, { emitEvent: false });
      isDirty ? ctrl.markAsDirty() : ctrl.markAsPristine();
      ctrl.updateValueAndValidity();
    });
  }

  /**
   * Erweiterungspunkt für Validierungsaktualisierungen.
   * Kann in abgeleiteten Klassen überschrieben werden.
   */
  protected onValidatorUpdate() {
    // Kann in Unterklassen implementiert werden
  }

  /**
   * Registriert eine Funktion, die aufgerufen wird, wenn sich der Wert des Formulars ändert.
   * @param fn Callback-Funktion
   */
  registerOnChange(fn: any): void {
    this.form.valueChanges.subscribe(fn);
  }

  /**
   * Registriert eine Funktion, die aufgerufen wird, wenn das Formular berührt wird.
   * @param fn Callback-Funktion
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Setzt den deaktivierten Zustand des Formulars.
   * @param isDisabled Boolean-Wert, der angibt, ob das Formular deaktiviert ist
   */
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
    this.onValidatorUpdate();
  }

  /**
   * Validiert das Formular.
   * @param ctrl Das zu validierende AbstractControl
   * @returns ValidationErrors oder null
   */
  validate(ctrl: AbstractControl): ValidationErrors | null {
    return null;
  }

  /**
   * Verdichtet Felder mit ähnlichem Namen, indem Null-Werte entfernt werden.
   * @param name Der Basename der Felder (z.B. "field" für "field1", "field2", "field3")
   */
  protected compress(name: string) {
    const values = [1, 2, 3].map(i => this.form.controls[`${name}${i}`]?.value);
    const newList = values.filter(v => v != null);
    const lastRawIdx = values.lastIndexOf(values.find(v => v != null));
    if (lastRawIdx + 1 !== newList.length) {
      const updatedValues: { [key: string]: any } = {};
      [1, 2, 3].forEach((i, idx) => {
        updatedValues[`${name}${i}`] = newList[idx] || null;
      });
      this.form.patchValue(updatedValues);
    }
  }

  /**
   * Wickelt eine FormGroup ein.
   * @param form Die einzuwickelnde FormGroup
   * @returns Die eingewickelte FormGroup
   */
  wrapFormGroup(form: FormGroup): FormGroup {
    return form;
  }

  /**
   * Handhabt Änderungen im Eingabefeld des Datum-Pickers.
   * @param fieldName Name des Feldes
   * @param event Das Input-Change-Event des Datum-Pickers
   */
  onDatePickerInputFieldChanged(fieldName: string, event: IMyInputFieldChanged) {
    if (event?.value) {
      const guessedBreakdown = this.dateFixer.convertToBreakdown(event.value);
      if (guessedBreakdown) {
        this.delayedDatePickerValues[fieldName] = {
          date: {
            year: guessedBreakdown.year,
            month: guessedBreakdown.month || 0,
            day: guessedBreakdown.day || 0
          }
        };
      }
    }
  }

  /**
   * Wird aufgerufen, wenn der Datum-Picker das Feld verliert.
   * Setzt den verzögerten Wert in das Formularfeld ein.
   * @param fieldName Name des Feldes
   */
  onDatePickerBlur(fieldName: string) {
    if (this.delayedDatePickerValues[fieldName]) {
      this.form.patchValue({ [fieldName]: this.delayedDatePickerValues[fieldName] });
      delete this.delayedDatePickerValues[fieldName];
    }
  }

  /**
   * Überprüft, ob ein Feld obligatorisch ist.
   * @param componentName Name des Feldes
   * @returns Boolean-Wert, der angibt, ob das Feld obligatorisch ist
   */
  isMandatory(componentName: string): boolean {
    const control = this.form.get(componentName);
    if (control?.validator) {
      const errors = control.validator({} as AbstractControl);
      return !!errors?.required;
    }
    return false;
  }

  /**
   * Überprüft, ob die Komponente sichtbar ist.
   * @returns Boolean-Wert, der angibt, ob die Komponente sichtbar ist
   */
  isVisible(): boolean {
    return true;
  }

  /**
   * Gibt die maximale Länge eines Eingabefeldes zurück.
   * @param componentName Name des Feldes
   * @returns Maximale Länge des Feldes im Backend oder einen Fallback-Wert
   */
  getMaxLength(componentName: string): number {
    const control = this.form.get(componentName);
    if (control?.validator) {
      // Das Feld hat einen Validator vom Backend bekommen

      // temporäres Feld erstellen und einen viel zu langen Text validieren lassen
      const testControl = new FormControl(this.longText);
      const errors = control.validator(testControl);

      // so lässt sich der maxLength-Wert auslesen
      if (errors?.maxlength?.requiredLength) {
        return errors.maxlength.requiredLength;
      }
    }

    // fallback-Wert für Felder, die keinen Validator vom Backend bekommen haben
    return 5000;
  }

  protected fillInFields(value : any) {
    value = value || {};
    const children = this.form.controls;
    if (children) {
      const fieldNames = Object.keys(children);
      for (const fieldName of fieldNames) {
        if (!value.hasOwnProperty(fieldName)) {
          const ctrl = children[fieldName];
          value[fieldName] = ctrl instanceof FormArray ? [] : null;
        }
      }
    }
    return value;
  }

  private createDummyValue(length: number): string {
    let dummyValue = '';
    for (let i = 0; i < length; i++) {
      dummyValue += '^';
    }
    return dummyValue;
  }

  /**
   * Deaktiviert den Assistenten.
   */
  disableAssistant() {
    this.assistantPopoverService.disableAssistant();
  }

  /**
   * Aktiviert den Assistenten.
   */
  enableAssistant() {
    this.assistantPopoverService.assistantService.enable();
  }

  /**
   * Gibt den AssistantService zurück.
   * @returns Instance des AssistantService
   */
  getAssistantService() {
    return this.assistantPopoverService.assistantService;
  }
}
